/**
 * ViewStatusBadge - Visual indicator for view operational status
 *
 * Displays a color-coded badge representing the view status.
 * Color scheme:
 * - active: Green - View is active and up-to-date
 * - building: Blue - View is currently being refreshed/built
 * - failed: Red - View refresh/build failed
 * - paused: Gray - View is paused and won't refresh
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import type { ViewStatus } from '@/types/views';

interface ViewStatusBadgeProps {
  /**
   * View operational status to display
   */
  status: ViewStatus;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Get custom color classes for view status badge
 */
const getColorClasses = (status: ViewStatus): string => {
  switch (status) {
    case 'active':
      return 'bg-success/10 text-success border-success/30';
    case 'building':
      return 'bg-info/10 text-info border-info/30';
    case 'failed':
      return 'bg-destructive/10 text-destructive border-destructive/30';
    case 'paused':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return '';
  }
};

/**
 * Get descriptive tooltip text for view status
 */
const getTooltipText = (status: ViewStatus): string => {
  switch (status) {
    case 'active':
      return 'View is active and available for querying';
    case 'building':
      return 'View is currently being refreshed or built';
    case 'failed':
      return 'View refresh or build failed - check logs for details';
    case 'paused':
      return 'View is paused and will not auto-refresh';
    default:
      return '';
  }
};

/**
 * Get display label for status
 */
const getStatusLabel = (status: ViewStatus): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'building':
      return 'Building';
    case 'failed':
      return 'Failed';
    case 'paused':
      return 'Paused';
    default:
      return status;
  }
};

/**
 * ViewStatusBadge Component
 *
 * Renders a styled badge indicating the view operational status.
 *
 * @example
 * ```tsx
 * <ViewStatusBadge status="active" />
 * <ViewStatusBadge status="building" className="ml-2" />
 * ```
 */
export const ViewStatusBadge: React.FC<ViewStatusBadgeProps> = ({ status, className }) => {
  const colorClasses = getColorClasses(status);
  const tooltipText = getTooltipText(status);
  const label = getStatusLabel(status);

  return (
    <Badge
      variant="outline"
      className={`${colorClasses} border font-semibold ${className || ''}`}
      title={tooltipText}
    >
      {label}
    </Badge>
  );
};
